import { WALL_THICKNESS, clamp } from './constants.js';
import { sceneWalls } from './siteGeometry.js';
import { inStairHole } from './stairs.js';
import { siteSurfaces } from './siteAreas.js';

export function ptInPoly(x, y, pts) {
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const xi = pts[i].x, yi = pts[i].y, xj = pts[j].x, yj = pts[j].y;
    if ((yi > y) !== (yj > y) && x < (xj - xi) * (y - yi) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

export function polyArea(pts) {
  let a = 0;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    a += (pts[j].x + pts[i].x) * (pts[j].y - pts[i].y);
  }
  return Math.abs(a / 2);
}

export function polyCentroid(pts) {
  let x = 0, y = 0;
  for (const p of pts) { x += p.x; y += p.y; }
  return { x: x / pts.length, y: y / pts.length };
}

export function polyBBox(pts) {
  let a = Infinity, b = Infinity, c = -Infinity, d = -Infinity;
  for (const p of pts) {
    a = Math.min(a, p.x); b = Math.min(b, p.y);
    c = Math.max(c, p.x); d = Math.max(d, p.y);
  }
  return { x0: a, y0: b, x1: c, y1: d };
}

/** Parametric hit of segment AB against segment CD; t along AB, or null. */
export function segHit(ax, ay, bx, by, cx, cy, dx, dy) {
  const r1 = bx - ax, r2 = by - ay, s1 = dx - cx, s2 = dy - cy;
  const den = r1 * s2 - r2 * s1;
  if (Math.abs(den) < 1e-12) return null;
  const t = ((cx - ax) * s2 - (cy - ay) * s1) / den;
  const u = ((cx - ax) * r2 - (cy - ay) * r1) / den;
  if (t < 0 || t > 1 || u < 0 || u > 1) return null;
  return t;
}

export function distToSeg(px, py, ax, ay, bx, by) {
  const dx = bx - ax, dy = by - ay, L = dx * dx + dy * dy;
  let t = L ? ((px - ax) * dx + (py - ay) * dy) / L : 0;
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}

export function insideSite(S, x, y) {
  let ok = false;
  for (const a of S.areas) if (a.kind === 'floor' && ptInPoly(x, y, a.pts)) { ok = true; break; }
  if (!ok) return false;
  for (const a of S.areas) if (a.kind === 'exclude' && ptInPoly(x, y, a.pts)) return false;
  return !inStairHole(S, x, y);
}

export function siteBBox(S) {
  const fl = S.areas.filter(a => a.kind === 'floor');
  if (!fl.length) return null;
  const b = polyBBox(fl[0].pts);
  for (const a of fl.slice(1)) {
    const q = polyBBox(a.pts);
    b.x0 = Math.min(b.x0, q.x0); b.y0 = Math.min(b.y0, q.y0);
    b.x1 = Math.max(b.x1, q.x1); b.y1 = Math.max(b.y1, q.y1);
  }
  return b;
}

export function siteArea(S) {
  return siteSurfaces(S).area;
}

/** Clip a sight line against the same wall prism used by the 3D renderer.
 * Local wall coordinates also handle parallel rays and cameras inside a wall. */
export function wallIntersection(cx, cy, ch, px, py, targetH, w) {
  if (Math.min(ch, targetH) > w.h || Math.max(ch, targetH) < (w.bottom || 0)) return null;
  const polygon = w.roofPts || w.wallPts;
  if (polygon) {
    const dz = targetH - ch;
    let enter = 0, leave = 1;
    if (Math.abs(dz) < 1e-12) { if (ch < w.bottom || ch > w.h) return null; }
    else { const a = (w.bottom - ch) / dz, b = (w.h - ch) / dz; enter = Math.max(0, Math.min(a, b)); leave = Math.min(1, Math.max(a, b)); }
    if (enter > leave) return null;
    const cuts = [enter, leave], dx = px - cx, dy = py - cy;
    for (const ring of [polygon, ...(w.roofHoles || [])]) for (let i = 0; i < ring.length; i++) {
      const a = ring[i], b = ring[(i + 1) % ring.length], ex = b.x - a.x, ey = b.y - a.y, den = dx * ey - dy * ex;
      if (Math.abs(den) < 1e-12) continue;
      const t = ((a.x - cx) * ey - (a.y - cy) * ex) / den, u = ((a.x - cx) * dy - (a.y - cy) * dx) / den;
      if (t > enter && t < leave && u >= 0 && u <= 1) cuts.push(t);
    }
    cuts.sort((a, b) => a - b);
    for (let i = 1; i < cuts.length; i++) {
      const mid = (cuts[i - 1] + cuts[i]) / 2;
      if (ptInPoly(cx + dx * mid, cy + dy * mid, polygon) && !(w.roofHoles || []).some(r => ptInPoly(cx + dx * mid, cy + dy * mid, r))) return { enter: cuts[i - 1], leave: cuts[i] };
    }
    return null;
  }
  const dx = w.x2 - w.x1, dy = w.y2 - w.y1, len = Math.hypot(dx, dy);
  if (len < 1e-6) return null;
  const ux = dx / len, uy = dy / len;
  const along = (x, y) => (x - w.x1) * ux + (y - w.y1) * uy;
  const across = (x, y) => -(x - w.x1) * uy + (y - w.y1) * ux;
  const a = [along(cx, cy), across(cx, cy), ch];
  const b = [along(px, py), across(px, py), targetH];
  const thickness = w.thickness ?? WALL_THICKNESS;
  const lo = [0, -thickness / 2, w.bottom || 0], hi = [len, thickness / 2, w.h];
  let enter = 0, leave = 1;
  for (let k = 0; k < 3; k++) {
    const d = b[k] - a[k];
    if (Math.abs(d) < 1e-12) {
      if (a[k] < lo[k] || a[k] > hi[k]) return null;
    } else {
      const t1 = (lo[k] - a[k]) / d, t2 = (hi[k] - a[k]) / d;
      enter = Math.max(enter, Math.min(t1, t2));
      leave = Math.min(leave, Math.max(t1, t2));
      if (enter > leave) return null;
    }
  }
  return { enter, leave };
}

/** Only a numerical endpoint tolerance, never a percentage of viewing range.
 * The old 1.5% exemption let cameras see through nearby interior walls. */
export function blocked(cx, cy, ch, px, py, targetH, walls) {
  for (let i = 0; i < walls.length; i++) {
    const w = walls[i];
    if (w.kind === 'glass' || Math.min(ch, targetH) > w.h || Math.max(ch, targetH) < (w.bottom || 0)) continue;
    const pad = (w.thickness ?? WALL_THICKNESS) / 2;
    if (Math.min(w.x1, w.x2) - pad > Math.max(cx, px) || Math.max(w.x1, w.x2) + pad < Math.min(cx, px) ||
        Math.min(w.y1, w.y2) - pad > Math.max(cy, py) || Math.max(w.y1, w.y2) + pad < Math.min(cy, py)) continue;
    const hit = wallIntersection(cx, cy, ch, px, py, targetH, w);
    if (hit && hit.leave > 1e-8 && hit.enter < 1 - 1e-8) return true;
  }
  return false;
}

export function wallsNear(S, cx, cy, r) {
  const out = [];
  for (const w of sceneWalls(S)) {
    if (w.kind === 'glass') continue;
    if (w.roofPts || w.wallPts) { if (Math.hypot(cx - clamp(cx, w.x1, w.x2), cy - clamp(cy, w.y1, w.y2)) <= r) out.push(w); continue; }
    if (distToSeg(cx, cy, w.x1, w.y1, w.x2, w.y2) <= r + (w.thickness || 0)) out.push(w);
  }
  return out;
}
